import React from "react";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import { useParams } from "react-router-dom";
import { useCurrency } from "@shared/selectors/globalSelectors";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@shared/ui/accordion";
import {
  Eye,
  ExternalLink,
  CreditCard,
  DollarSign,
  FileText,
  RotateCcw,
  Banknote,
} from "lucide-react";
import {
  usePaymentSingleBillDetail,
  usePaymentBillAllocations,
} from "../../../../hooks";
import { PaymentDetail } from "@features/cx/payments/types";

interface PaymentDetailsTabProps {
  payment?: PaymentDetail;
}

export const PaymentDetailsTab = ({
  payment: originalPaymentProp,
}: PaymentDetailsTabProps) => {
  const currency = useCurrency();
  const { id } = useParams<{ id: string }>();

  const { data: paymentData } = usePaymentSingleBillDetail(Number(id));

  const isMultiBill =
    paymentData?.transactionDetails?.linkedTo?.type === "MULTI_BILL";
  const { data: multiBillAllocations } = usePaymentBillAllocations(
    Number(id),
    isMultiBill,
  );

  const linkedMultiBills = paymentData?.transactionDetails?.linkedTo?.bills;

  const allocationRows =
    isMultiBill && multiBillAllocations
      ? Array.isArray(multiBillAllocations)
        ? multiBillAllocations
        : multiBillAllocations?.rows || []
      : paymentData?.paymentAllocation?.rows || [];

  const allocationTotals =
    isMultiBill && multiBillAllocations
      ? multiBillAllocations?.totals
      : paymentData?.paymentAllocation?.totals;

  // const {
  //   transactionDetails,
  //   cashDetails,
  //   paymentSummary,
  //   paymentAllocation,
  //   refunds,
  //   refundCount,
  // } = paymentData || {};

  const isCashOrCheck = ["cash", "check", "money order"].includes(
    (paymentData?.transactionDetails?.paymentMethod || "").toLowerCase(),
  );

  const isInstallment =
    (paymentData?.transactionDetails?.paymentType || "").toLowerCase() ===
      "installment" ||
    (paymentData?.paymentAllocation?.type || "").toLowerCase() ===
      "installment";

  const isRegistrationOrNsc =
    [
      "registration",
      "nsc charge",
      "nsc charges",
      "transfer",
      "transfer payment",
      "credit note",
      "service payment",
      "service_payment",
    ].includes(
      (paymentData?.transactionDetails?.paymentType || "").toLowerCase(),
    ) ||
    [
      "registration",
      "nsc charge",
      "nsc charges",
      "transfer",
      "transfer payment",
      "credit note",
      "service payment",
      "service_payment",
    ].includes((paymentData?.paymentAllocation?.type || "").toLowerCase());

  const hasRefunds = paymentData?.refunds && paymentData?.refunds.length > 0;

  const transactionDetailsList = [
    {
      label: "Consumer Name",
      value: paymentData?.transactionDetails?.consumerName || "N/A",
      className: "font-medium text-gray-900",
    },
    {
      label: "Consumer ID",
      value: paymentData?.transactionDetails?.consumerId || "N/A",
      className: "font-mono text-gray-900",
    },
    {
      label: "Payment Method",
      value:
        paymentData?.transactionDetails?.paymentMethodDisplay ||
        paymentData?.paymentMode ||
        originalPaymentProp?.paymentMode ||
        "N/A",
    },
    {
      label: "Payment Channel",
      value:
        paymentData?.transactionDetails?.paymentChannelDisplay ||
        originalPaymentProp?.paymentChannel ||
        "N/A",
    },
    {
      label: "Payment Type",
      value: paymentData?.transactionDetails?.paymentType || "N/A",
    },
    {
      label: "Receipt ID",
      value: paymentData?.transactionDetails?.receiptId || "N/A",
      className: "font-mono text-gray-900",
    },
    {
      label: "Payment ID",
      value: paymentData?.transactionDetails?.paymentId || "N/A",
      className: "font-mono text-[13px] text-gray-600",
    },
    {
      label: "Created On",
      value: paymentData?.transactionDetails?.createdOn || "N/A",
    },
    {
      label: "Created By",
      value: paymentData?.transactionDetails?.createdBy || "N/A",
    },
  ];

  const cashDetailsData = paymentData?.cashDetails || "N/A";

  const cashReceivedVal = cashDetailsData?.cashReceived || "N/A";
  const paymentDateVal = cashDetailsData?.paymentDate || "N/A";

  const cashDetailsList = [
    {
      label: "Cash Received",
      value: cashReceivedVal ? `${currency} ${cashReceivedVal}` : "N/A",
      className: "font-mono text-gray-900",
    },
    {
      label: "Payment Date",
      value: paymentDateVal || "N/A",
    },
  ];

  const standardAllocationColumns: TableColumn<any>[] = [
    {
      key: "billNumber",
      header: "Bill",
      width: "w-[25%] min-w-[25%] max-w-[25%]",
      render: (value: any, row: any) => (
        <div className="flex flex-col">
          <span className="font-mono font-bold text-gray-800">
            {value || "N/A"}
          </span>
          <span className="text-xs text-gray-500 mt-1">
            {row?.billingPeriod || "N/A"}
          </span>
        </div>
      ),
    },
    {
      key: "totalAmount",
      header: "Total Amount",
      width: "w-[15%] min-w-[15%] max-w-[15%]",
      render: (value: any) => (
        <div className="text-center font-medium">
          {currency} {value || "0.00"}
        </div>
      ),
    },
    {
      key: "outstandingBefore",
      header: "Outstanding Before",
      width: "w-[15%] min-w-[15%] max-w-[15%]",
      render: (value: any) => (
        <div className="text-center text-red-500 font-medium">
          {currency} {value || "0.00"}
        </div>
      ),
    },
    {
      key: "applied",
      header: "Applied",
      width: "w-[15%] min-w-[15%] max-w-[15%]",
      render: (value: any) => (
        <div className="text-center text-blue-600 font-bold">
          {currency} {value || "0.00"}
        </div>
      ),
    },
    {
      key: "outstandingAfter",
      header: "Outstanding After",
      width: "w-[15%] min-w-[15%] max-w-[15%]",
      render: (value: any) => (
        <div className="text-center text-green-600 font-medium">
          {currency} {value || "0.00"}
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      width: "w-[15%] min-w-[15%] max-w-[15%]",
      render: (value: any) => (
        <div className="flex justify-start">
          <Badge className="bg-green-100 text-green-700 hover:bg-green-200 border-none text-[10px] px-2 py-0 h-5 flex items-center justify-center">
            {value || "N/A"}
          </Badge>
        </div>
      ),
    },
  ];

  const installmentAllocationColumns: TableColumn<any>[] = [
    {
      key: "installment",
      header: "Installment",
      width: "w-[40%] min-w-[40%] max-w-[40%]",
      render: (value: any, row: any) => (
        <div className="flex flex-col">
          <span className="font-bold text-gray-900">
            {value || "Installment"}
          </span>
          <span className="text-[11px] text-gray-500 mt-0.5">
            {row.date || "N/A"}
          </span>
        </div>
      ),
    },
    {
      key: "totalAmount",
      header: "Total Amount",
      width: "w-[20%] min-w-[20%] max-w-[20%]",
      render: (value: any) => (
        <div className="text-center font-medium font-mono text-gray-900">
          {currency} {value || "0.00"}
        </div>
      ),
    },
    {
      key: "applied",
      header: "Applied",
      width: "w-[20%] min-w-[20%] max-w-[20%]",
      render: (value: any) => (
        <div className="text-center text-blue-600 font-bold font-mono">
          {currency} {value || "0.00"}
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      width: "w-[20%] min-w-[20%] max-w-[20%]",
      render: (value: any) => (
        <div className="flex justify-center">
          <Badge className="bg-green-100 text-green-700 hover:bg-green-200 border-none">
            {value || "N/A"}
          </Badge>
        </div>
      ),
    },
  ];

  const registrationAllocationColumns: TableColumn<any>[] = [
    {
      key: "item",
      header: "Item",
      width: "w-[40%] min-w-[40%] max-w-[40%]",
      render: (value: any, row: any) => {
        const itemLabel = row.label || value;
        let title =
          itemLabel ||
          row.reference ||
          row.billNumber ||
          paymentData?.transactionDetails?.receiptId ||
          "N/A";

        let subText = row.description;

        // Retain previous behavior for NSC charges specifically, if requested
        if (
          title.toLowerCase() === "nsc charge" ||
          title.toLowerCase() === "nsc charges"
        ) {
          title = paymentData?.transactionDetails?.receiptId || title;
          subText =
            row.date || paymentData?.transactionDetails?.createdOn || "N/A";
        } else if (!subText) {
          if (!itemLabel) {
            // Fallback for Transfer (no label/item provided)
            subText =
              row.date ||
              row.billingPeriod ||
              paymentData?.transactionDetails?.createdOn ||
              "N/A";
          }
        }

        return (
          <div className="flex flex-col">
            <span className="font-bold text-gray-900">{title || "N/A"}</span>
            <span className="text-[11px] text-gray-500 mt-0.5">
              {subText || "N/A"}
            </span>
          </div>
        );
      },
    },
    {
      key: "totalAmount",
      header: "Total Amount",
      width: "w-[20%] min-w-[20%] max-w-[20%]",
      render: (value: any) => (
        <div className="text-center font-medium font-mono text-gray-900">
          {currency} {value || "0.00"}
        </div>
      ),
    },
    {
      key: "applied",
      header: "Applied",
      width: "w-[20%] min-w-[20%] max-w-[20%]",
      render: (value: any) => (
        <div className="text-center text-blue-600 font-bold font-mono">
          {currency} {value || "0.00"}
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      width: "w-[20%] min-w-[20%] max-w-[20%]",
      render: (value: any) => (
        <div className="flex justify-center">
          <Badge className="bg-green-100 text-green-700 hover:bg-green-200 border-none">
            {value || "N/A"}
          </Badge>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Transaction Details Card */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex justify-between items-center p-4 border-b border-gray-100 bg-gray-50/50">
          <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
            <CreditCard className="w-[18px] h-[18px] text-gray-500" />
            Transaction Details
          </h3>
          {paymentData?.transactionDetails?.linkedTo?.statusBadge && (
            <Badge
              variant="outline"
              className="bg-green-50 text-green-700 border-none font-medium h-5"
            >
              {paymentData?.transactionDetails?.linkedTo?.statusBadge}
            </Badge>
          )}
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-x-12 gap-y-6">
            {transactionDetailsList.map((detail, index) => (
              <ValueLabel
                key={index}
                label={detail.label}
                value={detail.value}
                className={detail.className}
              />
            ))}

            {/* Linked To — full-width row, inner 4-col bill grid */}
            <div className="col-span-full flex flex-col gap-2 pt-2 border-t border-gray-100">
              <span className="text-[10px] font-bold text-gray-400 tracking-widest uppercase flex items-center gap-2">
                Linked To
                {paymentData?.transactionDetails?.linkedTo?.type ===
                  "MULTI_BILL" &&
                  linkedMultiBills?.length > 0 && (
                    <span className="text-[10px] font-semibold text-blue-500 normal-case tracking-normal">
                      · {linkedMultiBills.length} Bill
                      {linkedMultiBills.length !== 1 ? "s" : ""}
                    </span>
                  )}
              </span>

              {paymentData?.transactionDetails?.linkedTo?.type ===
                "MULTI_BILL" && linkedMultiBills?.length > 0 ? (
                <div className="grid grid-cols-4 gap-2">
                  {linkedMultiBills.map((bill: any, idx: number) => (
                    <div
                      key={bill.remoteBillId ?? idx}
                      className="flex flex-col gap-0.5 bg-blue-50 border border-blue-100 rounded-md px-3 py-2"
                    >
                      <span className="font-mono font-bold text-blue-700 text-xs flex items-center gap-1 truncate">
                        <FileText className="w-3 h-3 shrink-0" />
                        {bill.billNumber || bill.remoteBillId || "N/A"}
                      </span>
                      <span className="text-[11px] text-gray-500">
                        {bill.billingPeriod || "Current Period"}
                      </span>
                    </div>
                  ))}
                </div>
              ) : paymentData?.transactionDetails?.linkedTo?.type === "BILL" &&
                paymentData?.transactionDetails?.linkedTo?.remoteBillId !=
                  null ? (
                <div className="flex flex-col gap-1">
                  <span className="font-mono font-bold text-blue-700 text-xs flex items-center gap-1">
                    <FileText className="w-3 h-3 shrink-0" />
                    {paymentData?.transactionDetails?.linkedTo?.billNumber ||
                      paymentData?.transactionDetails?.linkedTo?.remoteBillId}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-gray-500">
                      {paymentData?.transactionDetails?.linkedTo
                        ?.billingPeriod || "Current Period"}
                    </span>
                    {paymentData?.transactionDetails?.linkedTo?.statusBadge && (
                      <Badge
                        variant="outline"
                        className="bg-green-50 text-green-700 border-none text-[10px] h-4 px-1.5 rounded"
                      >
                        {paymentData?.transactionDetails?.linkedTo?.statusBadge}
                      </Badge>
                    )}
                  </div>
                </div>
              ) : paymentData?.transactionDetails?.linkedTo?.billNumber ? (
                <div className="flex flex-col gap-1">
                  <span className="font-mono font-bold text-blue-700 text-xs flex items-center gap-1">
                    <FileText className="w-3 h-3 shrink-0" />
                    {paymentData?.transactionDetails?.linkedTo?.billNumber}
                  </span>
                  <span className="text-[11px] text-gray-500">
                    {paymentData?.transactionDetails?.linkedTo?.billingPeriod ||
                      "N/A"}
                  </span>
                </div>
              ) : (
                <span className="text-sm font-medium text-gray-900">—</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {Boolean(cashDetailsData) && (
        <Accordion
          type="single"
          collapsible
          defaultValue="cash-details"
          className="w-full"
        >
          <AccordionItem
            value="cash-details"
            className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden border-b-0"
          >
            <AccordionTrigger className="flex items-center justify-between p-4 bg-gray-50/50 hover:no-underline font-bold text-base text-gray-800">
              <div className="flex items-center gap-2">
                <Banknote className="w-[18px] h-[18px] text-gray-500" />
                Cash Details
              </div>
            </AccordionTrigger>
            <AccordionContent className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              {cashDetailsList.map((detail, index) => (
                <ValueLabel
                  key={index}
                  label={detail.label}
                  value={detail.value}
                  className={detail.className}
                />
              ))}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}

      {paymentData?.paymentSummary && !isInstallment && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="flex items-center p-4 border-b border-gray-100 bg-gray-50/50">
            <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
              <DollarSign className="w-[18px] h-[18px] text-gray-500" />
              Payment Summary
            </h3>
          </div>
          <div className="p-0">
            <div className="flex justify-between items-center px-6 py-3.5 border-b border-gray-100 text-[13px]">
              <span className="text-gray-600">
                {paymentData?.paymentSummary?.label || "Total Bill Amount"}
              </span>
              <span className="font-bold text-gray-900">
                {currency}{" "}
                {paymentData?.paymentSummary?.totalAmount ||
                  paymentData?.paymentSummary?.totalBillAmount ||
                  "0.00"}
              </span>
            </div>

            {paymentData?.paymentSummary?.lateFee &&
              paymentData?.paymentSummary?.lateFee !== "0.00" && (
                <div className="flex justify-between items-center px-6 py-3.5 border-b border-gray-100 text-[13px]">
                  <span className="text-gray-600 flex items-center gap-2">
                    Late Fee
                    {paymentData?.paymentSummary?.lateFeeWaived && (
                      <Badge
                        variant="outline"
                        className="bg-blue-50 text-blue-600 border-blue-200 h-5 px-1.5 rounded-sm"
                      >
                        Waived
                      </Badge>
                    )}
                  </span>
                  <span className="font-bold text-gray-900">
                    {currency} {paymentData?.paymentSummary?.lateFee}
                  </span>
                </div>
              )}

            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100">
              <span className="text-gray-900 font-bold text-[13px]">
                Amount Payable
              </span>
              <span className="font-bold text-[13px] text-gray-900">
                {currency}{" "}
                {paymentData?.paymentSummary?.amountPayable || "0.00"}
              </span>
            </div>

            <div className="flex justify-between items-center px-6 py-3.5 border-b border-gray-100 text-[13px] bg-gray-50/50">
              <span className="text-gray-600">Payment Made</span>
              <span className="font-bold text-gray-900">
                {currency} {paymentData?.paymentSummary?.paymentMade || "0.00"}
              </span>
            </div>

            <div className="flex justify-between items-center px-6 py-4 bg-green-50/30">
              <span className="text-green-700 font-bold text-[13px]">
                Outstanding Balance
              </span>
              <span className="font-bold text-[13px] text-green-700">
                {currency}{" "}
                {paymentData?.paymentSummary?.outstandingBalance || "0.00"}
              </span>
            </div>
          </div>
        </div>
      )}

      {paymentData?.paymentSummary && isInstallment && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="flex items-center p-4 border-b border-gray-100 bg-gray-50/50">
            <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
              <DollarSign className="w-[18px] h-[18px] text-gray-500" />
              Payment Summary
            </h3>
          </div>
          <div className="p-0">
            <div className="flex justify-between items-center px-6 py-3.5 border-b border-gray-100 text-[13px]">
              <span className="text-gray-600">Installment Amount</span>
              <span className="font-bold text-gray-900">
                {currency}{" "}
                {paymentData?.paymentSummary?.installmentAmount || "0.00"}
              </span>
            </div>

            <div className="flex justify-between items-center px-6 py-3.5 border-b border-gray-100 text-[13px]">
              <span className="text-gray-600">Installment No.</span>
              <span className="font-bold text-blue-600">
                {paymentData?.paymentSummary?.installmentNo || "1"}
              </span>
            </div>

            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100">
              <span className="text-gray-900 font-bold text-[13px]">
                Amount Payable
              </span>
              <span className="font-bold text-[13px] text-gray-900">
                {currency}{" "}
                {paymentData?.paymentSummary?.amountPayable || "0.00"}
              </span>
            </div>

            <div className="flex justify-between items-center px-6 py-3.5 border-b border-gray-100 text-[13px]">
              <span className="text-gray-600">Payment Made</span>
              <span className="font-bold text-gray-900">
                {currency} {paymentData?.paymentSummary?.paymentMade || "0.00"}
              </span>
            </div>

            <div className="flex justify-between items-center px-6 py-3.5 bg-yellow-50/30">
              <span className="text-yellow-700 font-bold text-[13px]">
                Remaining Agreement Balance
              </span>
              <span className="font-bold text-[13px] text-yellow-700">
                {currency}{" "}
                {paymentData?.paymentSummary?.remainingAgreementBalance ||
                  "0.00"}
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gray-50/50">
          <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
            <FileText className="w-[18px] h-[18px] text-gray-500" />
            Payment Allocation
          </h3>
          <Badge
            variant="outline"
            className="bg-blue-50 text-blue-700 border-blue-200 rounded-full font-medium"
          >
            {allocationRows?.length} bill(s)
          </Badge>
        </div>
        <div className="p-0">
          <GenericTable
            data={allocationRows || []}
            columns={
              isRegistrationOrNsc
                ? registrationAllocationColumns
                : isInstallment
                  ? installmentAllocationColumns
                  : standardAllocationColumns
            }
            rowKey={
              isRegistrationOrNsc
                ? "item"
                : isInstallment
                  ? "installment"
                  : "billNumber"
            }
            emptyMessage="No items found"
          />

          {(allocationTotals ||
            paymentData?.paymentAllocation?.totalApplied ||
            allocationRows?.length > 0) && (
            <div className="flex bg-gray-50 border-t border-gray-200 items-center w-full">
              <div
                className={`font-bold text-gray-900 px-4 py-4 text-left ${
                  isInstallment || isRegistrationOrNsc
                    ? "w-[40%] min-w-[40%] max-w-[40%]"
                    : "w-[25%] min-w-[25%] max-w-[25%]"
                }`}
              >
                {isInstallment || isRegistrationOrNsc
                  ? "Total Applied"
                  : "Totals"}
              </div>
              {isInstallment || isRegistrationOrNsc ? (
                <>
                  <div className="font-mono font-bold text-left w-[20%] min-w-[20%] max-w-[20%] px-4 py-4">
                    {currency}{" "}
                    {paymentData?.paymentAllocation?.totalAmount ||
                      allocationTotals?.totalAmount ||
                      allocationRows
                        ?.reduce(
                          (acc: number, r: any) =>
                            acc + Number(r.totalAmount || 0),
                          0,
                        )
                        .toFixed(2) ||
                      "0.00"}
                  </div>
                  <div className="font-mono font-bold text-left text-blue-600 w-[20%] min-w-[20%] max-w-[20%] px-4 py-4">
                    {currency}{" "}
                    {paymentData?.paymentAllocation?.totalApplied || "0.00"}
                  </div>
                  <div className="w-[20%] min-w-[20%] max-w-[20%] px-4 py-4"></div>
                </>
              ) : (
                <>
                  <div className="font-mono font-bold text-left w-[15%] min-w-[15%] max-w-[15%] px-4 py-4">
                    {currency} {allocationTotals?.totalAmount || "0.00"}
                  </div>
                  <div className="font-mono font-bold text-left text-red-500 w-[15%] min-w-[15%] max-w-[15%] px-4 py-4">
                    {currency} {allocationTotals?.outstandingBefore || "0.00"}
                  </div>
                  <div className="font-mono font-bold text-left text-blue-500 w-[15%] min-w-[15%] max-w-[15%] px-4 py-4">
                    {currency} {allocationTotals?.applied || "0.00"}
                  </div>
                  <div className="font-mono font-bold text-left text-green-600 w-[15%] min-w-[15%] max-w-[15%] px-4 py-4">
                    {currency} {allocationTotals?.outstandingAfter || "0.00"}
                  </div>
                  <div className="w-[15%] min-w-[15%] max-w-[15%] px-4 py-4 text-left"></div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {hasRefunds && (
        <div className="bg-white rounded-lg border border-gray-200 border-l-4 border-l-red-500 shadow-sm overflow-hidden">
          <div className="flex items-center p-4 border-b border-gray-100 bg-red-50/30">
            <h3 className="text-base font-bold text-red-800 flex items-center gap-2">
              Refunds ({paymentData?.refunds?.length})
            </h3>
          </div>
          <div className="p-0 flex flex-col divide-y divide-gray-100">
            {paymentData?.refunds?.map((refund: any, i: number) => (
              <div
                key={refund.refundId || i}
                className="flex items-center justify-between px-6 py-4"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <RotateCcw className="w-5 h-5 text-red-600" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="font-mono font-bold text-red-600 text-base">
                      {refund.refundId}
                    </span>
                    <div className="text-sm text-gray-500 font-medium">
                      {refund.reason || "N/A"} · {refund.paymentMode || "N/A"} ·{" "}
                      {refund.paymentDate}
                    </div>
                  </div>
                </div>

                <div className="font-mono font-bold text-xl text-red-600">
                  {currency} {refund.amount}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
